import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";

import messages from "../../../../../assets/locale/messages";
import { UICheckbox, UIDatePicker } from "../../../../../components/ui";

const ExportCheckbox = ({ name, checked, label, onChange }) => (
  <UICheckbox
    name={name}
    checked={checked}
    label={label}
    onChange={(nextChecked) =>
      onChange({ target: { name, checked: nextChecked } })
    }
    className="admin-applicants-export-form__checkbox"
  />
);

const ExportForm = ({
  selectedFields,
  handleChange,
  handleSelectAll,
  isAllSelected,
  handleSubmit,
  isSupervisor,
  openExportAll,
  handleGetDate,
  exportDate,
  dateError,
  fields: fieldsProp,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const defaultFields = [
    { name: "date", label: shared.date },
    { name: "applicantID", label: shared.id },
    { name: "imageUrl", label: shared.lists.image },
    { name: "name", label: shared.lists.name },
    { name: "dailyAmount", label: shared.dailyAmount },
    { name: "totalAmount", label: shared.totalAmount },
    { name: "stcNumber", label: shared.stcNumber },
    { name: "nationalId", label: events.exportApplicants.nationalId },
    { name: "gender", label: shared.gender },
    { name: "zone", label: events.creatEvent.zone },
    { name: "checkInStatus", label: events.viewEvents.checkinStatus },
    { name: "numberOfWorking", label: shared.numberOfWorkingDays },
    {
      name: "trainingDays",
      label: events.viewEvents.applicantsTab.trainingDays,
    },
    { name: "status", label: events.viewEvents.applicantsTab.status },
    { name: "role", label: events.viewEvents.applicantsTab.role },
    ...(isSupervisor || openExportAll
      ? [{ name: "note", label: shared.note }]
      : []),
    {
      name: "totalShiftAmount",
      label: events.viewEvents.applicantsTab.totalShiftAmount,
    },
    {
      name: "trainingDailyAmount",
      label: events.viewEvents.applicantsTab.trainingDailyAmount,
    },
    {
      name: "trainingTotalAmount",
      label: events.viewEvents.applicantsTab.trainingTotalAmount,
    },
    {
      name: "autoDeductionAmount",
      label: events.exportApplicants.autoDeductionAmount,
    },
    {
      name: "manualDeductionAmount",
      label: events.exportApplicants.manualDeductionAmount,
    },
    {
      name: "manualDeductionReason",
      label: events.exportApplicants.manualDeductionReason,
    },
    { name: "rewardAmount", label: events.exportApplicants.rewardAmount },
    { name: "rewardReason", label: events.exportApplicants.rewardReason },
    ...(isSupervisor || openExportAll
      ? [{ name: "phoneNumber", label: shared.phone_number }]
      : []),
  ];
  const fields = fieldsProp ?? defaultFields;

  return (
    <Box
      component="form"
      className="admin-applicants-export-form"
      onSubmit={handleSubmit}
    >
      <Box
        className="admin-applicants-export-form__select-all"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography className="admin-applicants-export-form__select-all-label">
          {events.viewEvents.applicantsTab.selectAll}
        </Typography>
        <UICheckbox
          checked={isAllSelected}
          label=""
          onChange={(nextChecked) =>
            handleSelectAll({ target: { checked: nextChecked } })
          }
          className="admin-applicants-export-form__checkbox"
        />
      </Box>

      <Box className="admin-applicants-export-form__grid">
        {fields.map((field) => (
          <React.Fragment key={field.name}>
            <ExportCheckbox
              name={field.name}
              checked={Boolean(selectedFields[field.name])}
              label={field.label}
              onChange={handleChange}
            />
            {field.name === "date" && selectedFields.date && (
              <Box className="admin-applicants-export-form__date">
                <UIDatePicker
                  value={exportDate}
                  onChange={handleGetDate}
                  name={selectedFields.selectedDate}
                  label={shared.selectDate}
                  required
                  error={dateError}
                  helperText={dateError ? "required" : null}
                  classNames={{
                    root: "admin-applicants-export-form__date-picker",
                  }}
                />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

ExportCheckbox.propTypes = {
  name: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  label: PropTypes.node.isRequired,
  onChange: PropTypes.func.isRequired,
};

ExportForm.propTypes = {
  handleSubmit: PropTypes.func,
  selectedFields: PropTypes.object,
  handleChange: PropTypes.func,
  handleSelectAll: PropTypes.func,
  isAllSelected: PropTypes.bool,
  isSupervisor: PropTypes.bool,
  openExportAll: PropTypes.bool,
  exportDate: PropTypes.string,
  handleGetDate: PropTypes.func,
  dateError: PropTypes.bool,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    }),
  ),
};

ExportForm.defaultProps = {
  handleSubmit: () => {},
  selectedFields: {},
  handleChange: () => {},
  handleSelectAll: () => {},
  isAllSelected: false,
  isSupervisor: false,
  openExportAll: false,
  exportDate: undefined,
  handleGetDate: () => {},
  dateError: false,
  fields: undefined,
};

export default ExportForm;
